import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdInfoOutline,
    MdLock,
    MdMarkEmailRead,
    MdMarkEmailUnread,
} from 'react-icons/md'
import {
    EmailPreference,
    EmailPreferencesResponse,
    getEmailPreferences,
    setAllEmailPreferences,
    updateEmailPreferences,
} from '@/lib/api/email-preferences/email-preferences-api'
import { useAuth } from '@/store/hooks'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

/**
 * Grupos que só existem na saúde. Prontuário e internação não têm equivalente
 * num salão, numa academia ou numa oficina — o backend segue aceitando a
 * preferência (ela continua valendo se a empresa mudar de segmento), aqui é só
 * para a tela não oferecer "Termo de consentimento" a quem não atende paciente.
 */
const HEALTH_ONLY_GROUPS: EmailPreference['group'][] = ['medical', 'hospital']

/**
 * Liga e desliga cada tipo de e-mail que a plataforma dispara pela filial ativa.
 *
 * A preferência é da EMPRESA, não de quem está olhando: os e-mails que mais
 * pesam na caixa ("Nova Cobrança Criada", resumo de vencidas) são endereçados ao
 * e-mail da empresa, então desligar por usuário não pararia nada.
 */
export default function SystemEmailsCard({ canEdit }: { canEdit: boolean }) {
    const { user } = useAuth()
    const { clientLabel } = useClientLabel()
    const serviceLabels = useServiceLabel()
    const isHealth = user?.company?.segment === 'HEALTH'
    const [data, setData] = useState<EmailPreferencesResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [savingKey, setSavingKey] = useState<string | null>(null)
    const [bulkSaving, setBulkSaving] = useState(false)

    useEffect(() => {
        let active = true
        const load = async () => {
            setLoading(true)
            const response = await getEmailPreferences()
            if (!active) return
            if (response.success) {
                setData(response.result)
            } else {
                toast.error(response.error || 'Não foi possível carregar os e-mails do sistema')
            }
            setLoading(false)
        }
        load()
        return () => {
            active = false
        }
    }, [])

    /**
     * O catálogo vem do backend com o vocabulário da saúde. Fora dela, "paciente"
     * vira "cliente" e "teleconsulta" vira o termo do segmento — o mesmo que
     * `segment-labels` faz com o menu.
     */
    const translate = (text: string) =>
        isHealth
            ? text
            : text
                  .replace(/pacientes/g, `${clientLabel}s`)
                  .replace(/paciente/g, clientLabel)
                  .replace(/Pacientes/g, 'Clientes')
                  .replace(/Paciente/g, 'Cliente')
                  .replace(/teleconsulta/g, serviceLabels.teleconsultation)
                  .replace(/Teleconsulta/g, serviceLabels.Teleconsultation)
                  .replace(/consultas/g, serviceLabels.consultations)
                  .replace(/consulta/g, serviceLabels.consultation)
                  .replace(/Consulta/g, serviceLabels.Consultation)

    const visibleCategories = useMemo(
        () =>
            (data?.categories ?? []).filter(
                category => isHealth || !HEALTH_ONLY_GROUPS.includes(category.group)
            ),
        [data, isHealth]
    )

    const grouped = useMemo(() => {
        if (!data) return []
        const order = Object.keys(data.groups) as EmailPreference['group'][]
        return order
            .map(group => ({
                group,
                ...data.groups[group],
                categories: visibleCategories.filter(category => category.group === group),
            }))
            .filter(section => section.categories.length > 0)
    }, [data, visibleCategories])

    const switchableCount = visibleCategories.filter(category => !category.alwaysOn).length
    const enabledCount = visibleCategories.filter(
        category => !category.alwaysOn && category.enabled
    ).length

    /** "Para o paciente" na saúde, "Para o cliente" nos demais segmentos. */
    const audienceLabel = (audience: EmailPreference['audience']) => {
        if (audience === 'INTERNAL') return 'Para a equipe'
        if (audience === 'PARTNER') return 'Para o parceiro'
        return `Para o ${clientLabel}`
    }

    const applyResult = (categories: EmailPreference[]) => {
        setData(previous => (previous ? { ...previous, categories } : previous))
    }

    const toggleCategory = async (category: EmailPreference, enabled: boolean) => {
        setSavingKey(category.key)
        const response = await updateEmailPreferences([{ category: category.key, enabled }])
        if (response.success) {
            applyResult(response.result)
        } else {
            toast.error(response.error || 'Não foi possível salvar a preferência')
        }
        setSavingKey(null)
    }

    const toggleAll = async (enabled: boolean) => {
        setBulkSaving(true)
        const response = await setAllEmailPreferences(enabled)
        if (response.success) {
            applyResult(response.result)
            toast.success(
                enabled
                    ? 'Todos os e-mails do sistema foram ativados'
                    : 'Todos os e-mails do sistema foram desativados'
            )
        } else {
            toast.error(response.error || 'Não foi possível salvar as preferências')
        }
        setBulkSaving(false)
    }

    if (loading) {
        return (
            <div className="card bg-base-200/50 border border-base-300">
                <div className="card-body items-center">
                    <span className="loading loading-spinner loading-md"></span>
                </div>
            </div>
        )
    }

    if (!data) return null

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        E-mails do sistema
                    </h2>
                    <p className="mt-1 text-sm text-base-content/60">
                        Escolha o que a plataforma envia por e-mail. Vale para a filial atual e
                        para todos os destinatários dela — não só para você.
                    </p>
                </div>

                {canEdit && (
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => toggleAll(true)}
                            disabled={bulkSaving}
                        >
                            <MdMarkEmailRead className="text-lg" />
                            Ativar todos
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-error btn-outline"
                            onClick={() => toggleAll(false)}
                            disabled={bulkSaving}
                        >
                            <MdMarkEmailUnread className="text-lg" />
                            Desativar todos
                        </button>
                    </div>
                )}
            </div>

            <div className="alert alert-info">
                <MdInfoOutline className="text-xl shrink-0" />
                <span>
                    {enabledCount} de {switchableCount} tipos de e-mail estão ativos. Os e-mails
                    de acesso à conta — senha, confirmação e código de acesso — são sempre
                    enviados e não podem ser desligados.
                </span>
            </div>

            {!canEdit && (
                <div className="alert alert-warning">
                    <MdLock className="text-xl shrink-0" />
                    <span>
                        Somente a conta administradora da empresa pode alterar estas opções.
                    </span>
                </div>
            )}

            {grouped.map(section => (
                <div
                    key={section.group}
                    className="card bg-base-200/50 border border-base-300"
                >
                    <div className="card-body gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-base-content">
                                {section.label}
                            </h3>
                            <p className="text-sm text-base-content/60">
                                {translate(section.description)}
                            </p>
                        </div>

                        <div className="divide-y divide-base-300">
                            {section.categories.map(category => (
                                <div
                                    key={category.key}
                                    className="flex items-start justify-between gap-6 py-4 first:pt-0 last:pb-0"
                                >
                                    <div className="flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-medium text-base-content">
                                                {translate(category.label)}
                                            </span>
                                            <span className="badge badge-ghost badge-sm">
                                                {audienceLabel(category.audience)}
                                            </span>
                                        </div>
                                        <p className="mt-1 text-sm text-base-content/60">
                                            {translate(category.description)}
                                        </p>
                                    </div>

                                    {category.alwaysOn ? (
                                        <span className="flex shrink-0 items-center gap-2 text-sm text-base-content/50">
                                            <MdLock className="text-lg" />
                                            Sempre ativo
                                        </span>
                                    ) : savingKey === category.key ? (
                                        <span className="loading loading-spinner loading-sm shrink-0"></span>
                                    ) : (
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-secondary shrink-0"
                                            checked={category.enabled}
                                            disabled={!canEdit || bulkSaving}
                                            onChange={event =>
                                                toggleCategory(category, event.target.checked)
                                            }
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}
